// @flow
import React, { Component, Fragment } from "react";
import { bindAll, noop, isEqual, get } from "lodash";
import formatCurrency from "@tvg/formatter/currency";
import MyBetsWager from "@tvg/my-bets-standalone/src/MyBetsWager";

import Button from "../../_atom/Buttons/button";
import ProcessingOverlay from "../../_static/ProcessingOverlay";
import RaceLink from "../../_molecule/RaceLink";
import BetSummary from "../../_molecule/BetSummary";
import MyBetsCard from "../../_organism/MyBetsCard";
import type { Props, State } from "./types";
import {
  Wrapper,
  ConfirmationButtonContainer,
  BetSummaryContainer,
  RetainButton,
  WarningMessageWrapper,
  WagerAmountContainer,
  TotalAmountText,
  WarningMessage
} from "./styled-components";

export class BetConfirmation extends Component<Props, State> {
  static defaultProps = {
    postTime: "",
    mtp: 0,
    raceNumber: "1",
    raceTypeCode: "T",
    trackName: "",
    isGreyhound: false,
    betTotal: 0,
    isCancelable: false,
    wagerAmount: 0,
    wagerTypeName: "",
    wagerCode: "",
    selections: [],
    onConfirmation: noop,
    feedbackState: "none",
    feedbackMessage: "placing bet...",
    isKey: false,
    isLeg: false,
    isBox: false,
    isBetPlaced: false,
    isBettingError: false,
    isProcessing: false,
    hasRaceAlertsBar: false,
    errorBetMessage: "",
    onRetain: noop,
    onBackToRace: noop,
    confirmText: "Confirm $amount Bet",
    favoriteRunners: [],
    repetitionsNr: 1,
    onRepeatClick: noop,
    hasRepeaterEnabled: false,
    renderRaceAlerts: noop,
    showMyBetsCardDesign: false,
    probableValue: "",
    showRepeatButton: true
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: false
    };

    bindAll(this, [
      "handleConfirmation",
      "renderButtons",
      "renderBetSummary",
      "renderLegacyBetSummary"
    ]);
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return (
      this.state.isLoading !== nextState.isLoading ||
      this.props.selections.length !== nextProps.selections.length ||
      this.props.repetitionsNr !== nextProps.repetitionsNr ||
      this.props.hasRaceAlertsBar !== nextProps.hasRaceAlertsBar ||
      !isEqual(this.props, nextProps)
    );
  }

  handleConfirmation() {
    this.setState({ isLoading: true });

    this.props.onConfirmation();
  }

  renderButtons() {
    if (this.props.isBetPlaced && !this.props.isBettingError) {
      return (
        <Fragment>
          <RetainButton
            onClick={this.props.onRetain}
            qaLabel="betConfirmation-retainRunnersBtn"
          >
            RETAIN RUNNERS
          </RetainButton>
          <Button
            onClick={this.props.onBackToRace}
            type="primary"
            size="big"
            qaLabel="betConfirmation-backToRaceBtn"
          >
            BACK TO RACE
          </Button>
        </Fragment>
      );
    }
    if (this.props.isBettingError) {
      return (
        <Button
          type="primary"
          size="big"
          isStretched
          onClick={this.props.onRetain}
          qaLabel="betConfirmation-closeBtn"
        >
          close
        </Button>
      );
    }
    return (
      <Button
        type="bet"
        size="big"
        isStretched
        onClick={this.handleConfirmation}
        qaLabel="betConfirmation-confirmationButton"
      >
        <WagerAmountContainer>
          <span>{this.props.confirmText.split("$amount")[0]}</span>
          <TotalAmountText>
            {formatCurrency(get(this.props, "betTotal", 0))}
          </TotalAmountText>
          <span>{this.props.confirmText.split("$amount")[1]}</span>
        </WagerAmountContainer>
      </Button>
    );
  }

  renderBetSummary() {
    return (
      <MyBetsCard
        showBetInfoHeader={false}
        showRaceLeg={this.props.isLeg}
        mtp={this.props.mtp}
        isRaceNear={this.props.mtp <= 5}
        isReplayInsideMyBets={
          this.props.hasRepeaterEnabled && !this.props.isBettingError
        }
        isMTPNewRules
        racePostTime={this.props.postTime}
        trackName={this.props.trackName}
        raceNumber={+this.props.raceNumber}
        legNumber={1}
        betsNumber={this.props.repetitionsNr}
        betAmount={this.props.betTotal}
        dateDiff={this.props.dateDiff}
        hasRepeatBet={this.props.showRepeatButton}
        onRepeatBet={this.props.onRepeatClick}
      >
        <MyBetsWager
          wager={this.props.wager}
          raceNumber={+this.props.raceNumber}
          betAmount={this.props.betTotal}
          bettingInterests={this.props.bettingInterests}
          hasFooter={false}
          hasDetails={false}
          isKey={this.props.isKey}
          isBox={this.props.isBox}
          isBetPlaced={this.props.isBetPlaced}
          isBetConfirmation
          betsNumber={this.props.repetitionsNr}
        />
      </MyBetsCard>
    );
  }

  renderLegacyBetSummary() {
    return (
      <React.Fragment>
        <RaceLink
          mtp={this.props.mtp}
          postTime={this.props.postTime}
          trackName={this.props.trackName}
          raceNumber={this.props.raceNumber}
          isGreyhound={this.props.isGreyhound}
          isLink={false}
        />
        <BetSummaryContainer>
          <BetSummary
            isCancelable={false}
            isRepeatable
            betTotal={this.props.betTotal}
            favoriteRunners={this.props.favoriteRunners}
            selectionTypeName={this.props.wagerTypeName}
            wagerAmount={this.props.wagerAmount}
            selections={this.props.selections}
            raceTypeCode={this.props.raceTypeCode}
            isKey={this.props.isKey}
            isLeg={this.props.isLeg}
            raceNumber={this.props.raceNumber}
            isVertical
            repetitionsNr={this.props.repetitionsNr}
            onRepeatClick={this.props.onRepeatClick}
            hasRepeaterEnabled={
              this.props.hasRepeaterEnabled && !this.props.isBettingError
            }
          />
        </BetSummaryContainer>
      </React.Fragment>
    );
  }

  renderFeedback() {
    let message = get(this.props, "errorBetMessage");
    let type = "error";
    let title = "";

    if (this.props.hasFeedback) {
      type = get(this.props, "feedbackType", "error");
      message = !this.props.partialFailure ? (
        <span data-qa-label="repeatBets-infoMessage">
          <span
            dangerouslySetInnerHTML={{
              __html: this.props.feedbackMsg
            }}
          />
        </span>
      ) : (
        <span data-qa-label="repeatBets-infoMessage">
          {this.props.partialInfo}
          <br />
          <span
            dangerouslySetInnerHTML={{
              __html: this.props.partialInfoDetails
            }}
          />
        </span>
      );
      title = this.props.feedbackTitle;
    }

    return (
      <WarningMessageWrapper>
        <WarningMessage
          title={title}
          type={type}
          message={message}
          qaLabel="bet-confirmation-warning"
        />
      </WarningMessageWrapper>
    );
  }

  render() {
    return (
      <Fragment>
        {this.props.isProcessing && <ProcessingOverlay />}
        <Wrapper data-qa-label="betConfirmation">
          {(this.props.isBettingError || this.props.hasFeedback) &&
            this.renderFeedback()}
          {this.props.showMyBetsCardDesign
            ? this.renderBetSummary()
            : this.renderLegacyBetSummary()}
          {this.props.hasRaceAlertsBar && this.props.renderRaceAlerts()}
          <ConfirmationButtonContainer
            hasTopMargin={!this.props.hasRaceAlertsBar}
          >
            {this.renderButtons()}
          </ConfirmationButtonContainer>
        </Wrapper>
      </Fragment>
    );
  }
}

export default BetConfirmation;
